'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { INSTRUMENTS } from '@/constants/instruments';
import { ArrowRight, BadgeCheck, Euro, MapPin, Music, Star } from 'lucide-react';
import { trackDirectoryCardClick, trackDirectoryCardImpression, type DirectoryCardLayout } from '@/lib/directoryCards';
import { TYPE_COLORS, TYPE_CTAS, TYPE_ICONS, TYPE_LABELS, type DirectoryType } from '@/constants/directoryTypes';

export interface DirectoryCardListing {
  id: string;
  name: string;
  slug: string;
  type: DirectoryType;
  city: string;
  province?: string;
  rating?: number | null;
  review_count?: number;
  is_verified?: boolean;
  is_promoted?: boolean;
  category?: string | null;
  services?: string[] | null;
  instruments?: string[];
  price_range?: string | null;
  price_source_url?: string | null;
  price_verified_at?: string | null;
  logo_url?: string | null;
  photos?: Array<{ url: string; alt?: string }> | null;
}

function hasRecentPriceEvidence(listing: DirectoryCardListing) {
  if (!listing.price_range || !listing.price_source_url || !listing.price_verified_at) return false;
  try {
    const source = new URL(listing.price_source_url);
    const verifiedAt = new Date(listing.price_verified_at).getTime();
    const ageMs = Date.now() - verifiedAt;
    return (source.protocol === 'https:' || source.protocol === 'http:')
      && Number.isFinite(verifiedAt)
      && ageMs >= 0
      && ageMs <= 180 * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

function isUnstableGoogleBusinessAsset(url?: string | null) {
  // These temporary Google Business CDN URLs commonly expire or throttle with
  // 403/429 responses. Prefer another supplied photo instead of requesting a
  // resource that will visibly break and pollute the browser console.
  return Boolean(url?.includes('lh3.googleusercontent.com/gps-cs-s/'));
}

export function DirectoryListingCard({
  listing,
  layout = 'grid',
  position,
}: {
  listing: DirectoryCardListing;
  layout?: DirectoryCardLayout;
  position: number;
}) {
  const cardRef = useRef<HTMLAnchorElement>(null);
  const didTrackImpression = useRef(false);
  const [imgFailed, setImgFailed] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);
  const Icon = TYPE_ICONS[listing.type] ?? Music;
  const color = TYPE_COLORS[listing.type];
  const photo = listing.photos?.find(item => item.url && !isUnstableGoogleBusinessAsset(item.url));
  const photoUrl = photo?.url;
  const photoPosition = listing.name.trim().toLocaleLowerCase('es') === 'la musicoteca'
    ? 'center 32%'
    : undefined;
  const logoUrl = isUnstableGoogleBusinessAsset(listing.logo_url) ? null : listing.logo_url;
  const device = typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches ? 'mobile' : 'desktop';
  const context = useMemo(() => ({ listingId: listing.id, listingType: listing.type, position, layout, device } as const), [device, layout, listing.id, listing.type, position]);

  useEffect(() => {
    const element = cardRef.current;
    if (!element || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && entry.intersectionRatio >= 0.5 && !didTrackImpression.current) {
        didTrackImpression.current = true;
        trackDirectoryCardImpression(context);
        observer.disconnect();
      }
    }, { threshold: 0.5 });
    observer.observe(element);
    return () => observer.disconnect();
  }, [context]);

  const location = `${listing.city}${listing.province && listing.province.toLocaleLowerCase() !== listing.city.toLocaleLowerCase() ? `, ${listing.province}` : ''}`;
  const instrumentItems = Array.from(new Set((listing.instruments || []).filter(Boolean).map(value => {
    const key = value.trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    return key === 'voz' ? 'canto' : key;
  }))).map(key => ({ key, label: INSTRUMENTS[key]?.labelShort || key.replaceAll('_', ' '), icon: INSTRUMENTS[key]?.icon || Music }));
  // La verificación del negocio no verifica su tarifa. Solo mostramos una fuente fechada.
  const price = hasRecentPriceEvidence(listing)
    ? `Precio orientativo: ${listing.price_range}`
    : undefined;
  const details = [{ text: location, icon: MapPin }, { text: price, icon: Euro }].filter(item => item.text);

  return (
    <Link
      ref={cardRef}
      href={`/directorio/${listing.slug}`}
      onClick={() => trackDirectoryCardClick(context)}
      className={`group relative flex w-full overflow-hidden rounded-[var(--radius-card)] border bg-white text-left transition hover:-translate-y-0.5 hover:border-violet-200 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-600 ${
        listing.is_promoted ? 'border-2 border-[var(--color-brand)] ring-1 ring-[var(--color-brand-light)]' : 'border-gray-200'
      } ${layout === 'list' ? 'h-[192px] flex-row' : 'h-full flex-col'}`}
    >
      <div className={`relative shrink-0 overflow-hidden ${layout === 'list' ? 'my-2 ml-2 mr-3 min-h-[96px] w-[112px] self-stretch rounded-[calc(var(--radius-card)-0.5rem)] sm:w-[136px]' : 'mx-2 mt-2 aspect-[16/10] rounded-[calc(var(--radius-card)-0.5rem)]'}`} style={{ backgroundColor: `${color}12` }}>
        {photoUrl && !imgFailed ? (
          <Image
            src={photoUrl}
            alt={photo?.alt || listing.name}
            fill
            sizes={layout === 'list' ? '(max-width: 767px) 112px, 136px' : '(max-width: 1023px) 50vw, 270px'}
            quality={70}
            loading={position <= 2 ? 'eager' : 'lazy'}
            fetchPriority={position <= 2 ? 'high' : 'auto'}
            onError={() => setImgFailed(true)}
            className="object-cover transition-transform duration-300 group-hover:scale-[1.03]"
            style={{ objectPosition: photoPosition }}
          />
        ) : <div className="absolute inset-0 flex items-center justify-center"><Icon className="h-9 w-9" style={{ color }} /></div>}
        {listing.is_promoted && <span className="absolute left-2 top-2 rounded-full bg-[var(--color-brand)] px-2 py-0.5 text-[10px] font-semibold text-white">Destacado</span>}
        <div className="absolute bottom-1.5 right-1.5 flex h-10 w-10 items-center justify-center overflow-hidden rounded-lg border border-white bg-white shadow-sm">
          {logoUrl && !logoFailed ? <Image src={logoUrl} alt="" fill sizes="40px" quality={65} loading="lazy" onError={() => setLogoFailed(true)} className="object-cover" /> : <Icon className="h-5 w-5" style={{ color }} />}
        </div>
      </div>
      <div className={`flex min-w-0 flex-1 flex-col overflow-hidden ${layout === 'list' ? 'py-3 pr-3' : 'p-3'}`}>
        <div className="flex items-center justify-between gap-1 text-[10px] font-medium md:flex-wrap md:gap-x-2 md:gap-y-1 md:text-xs" style={{ color }}>
          <span>{listing.category || TYPE_LABELS[listing.type]}</span>
          {listing.is_verified && <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-[var(--color-brand-light)] px-1 py-0.5 text-[9px] font-semibold md:px-1.5 md:text-[10px] text-[var(--color-brand)]"><BadgeCheck className="h-3.5 w-3.5" aria-hidden="true" />Verificado</span>}
        </div>
        <h2 className={`mt-1 font-semibold text-gray-900 group-hover:text-violet-700 ${layout === 'list' ? 'line-clamp-1 text-base' : 'line-clamp-2 min-h-10 text-sm'}`}>{listing.name}</h2>
        <div className="mt-2 flex min-w-0 flex-nowrap gap-x-3 overflow-hidden text-xs text-gray-600">
          {details.map(({text,icon: DetailIcon}) => <span key={text} className="inline-flex min-w-0 items-center gap-1 truncate"><DetailIcon className="h-3.5 w-3.5 shrink-0" />{text}</span>)}
        </div>
        {instrumentItems.length > 0 && (
          <div className="mt-1.5 flex flex-nowrap items-center gap-0.5 overflow-hidden text-gray-600 md:gap-1" aria-label="Instrumentos">
            {instrumentItems.slice(0, 7).map(({ key, label, icon: InstrumentIcon }) => (
              <span key={key} role="img" aria-label={label} title={label} className="inline-flex h-5 w-5 items-center justify-center rounded border border-gray-200 bg-white md:h-[22px] md:w-[22px]">
                <InstrumentIcon className="h-4 w-4" aria-hidden="true" />
              </span>
            ))}
            {instrumentItems.length > 7 && <span className="text-[10px] font-medium" title={instrumentItems.slice(7).map(item => item.label).join(', ')} aria-label={instrumentItems.slice(7).map(item => item.label).join(', ')}>+{instrumentItems.length - 7}</span>}
          </div>
        )}
        <div className="directory-card-footer mt-auto flex min-h-10 flex-nowrap items-center justify-between gap-2 pt-3">
          {listing.rating ? <span className="inline-flex items-center gap-1 text-xs text-gray-700"><Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" /><span className="font-semibold">{listing.rating}</span>{listing.review_count ? <span className="text-gray-600">({listing.review_count})</span> : null}</span> : <span />}
          <span className="ml-auto inline-flex min-h-8 shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg bg-[var(--color-brand)] px-2.5 py-1.5 text-xs font-semibold text-white transition-colors group-hover:bg-[var(--color-brand-dark)]">{TYPE_CTAS[listing.type]}<ArrowRight className="h-3.5 w-3.5" /></span>
        </div>
      </div>
    </Link>
  );
}
